<script setup lang="ts">
const route = useRoute()
const authStore = useAuthStore()
const cartStore = useCartStore()
const { t, locale, locales, setLocale } = useI18n()

const items = computed(() => [{
  label: t('nav.events'),
  to: '/catalog',
  active: route.path.startsWith('/catalog')
}])

const otherLocale = computed(() => {
  return locales.value.find((l: { code: string }) => l.code !== locale.value)
})
</script>

<template>
  <UHeader>
    <template #left>
      <NuxtLink to="/">
        <AppLogo class="w-auto h-7 shrink-0" />
      </NuxtLink>
    </template>

    <UNavigationMenu
      :items="items"
      variant="link"
    />

    <template #right>
      <NotificationBell v-if="authStore.isLoggedIn" />
      <NuxtLink
        v-if="authStore.isLoggedIn"
        to="/app/cart"
        class="relative"
      >
        <UButton
          icon="i-lucide-list-checks"
          color="neutral"
          variant="ghost"
        />
        <span
          v-if="cartStore.count > 0"
          class="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary-600 text-white text-xs flex items-center justify-center font-bold"
        >
          {{ cartStore.count }}
        </span>
      </NuxtLink>
      <UButton
        v-if="otherLocale"
        :label="otherLocale?.name"
        icon="i-lucide-languages"
        color="neutral"
        variant="ghost"
        size="sm"
        class="hidden sm:inline-flex"
        @click="otherLocale && setLocale(otherLocale.code)"
      />
      <UColorModeButton class="hidden sm:inline-flex" />

      <template v-if="authStore.isLoggedIn">
        <UButton
          :label="t('common.dashboard')"
          icon="i-lucide-layout-dashboard"
          size="sm"
          color="primary"
          to="/app/dashboard"
        />
        <UBadge
          :label="authStore.currentActor.name"
          color="primary"
          variant="subtle"
          class="hidden lg:inline-flex"
        />
        <UButton
          :label="t('common.signOut')"
          icon="i-lucide-log-out"
          size="sm"
          color="neutral"
          variant="ghost"
          class="hidden sm:inline-flex"
          @click="async () => { await useSession().logout(); await navigateTo('/') }"
        />
      </template>
      <template v-else>
        <UButton
          :aria-label="t('common.signIn')"
          icon="i-lucide-log-in"
          color="neutral"
          variant="ghost"
          to="/login"
          class="lg:hidden"
        />

        <UButton
          :label="t('common.signIn')"
          color="neutral"
          variant="outline"
          to="/login"
          class="hidden lg:inline-flex"
        />

        <UButton
          :label="t('common.register')"
          color="primary"
          trailing-icon="i-lucide-zap"
          class="hidden lg:inline-flex"
          to="/signup"
        />
      </template>
    </template>

    <template #body>
      <UNavigationMenu
        :items="items"
        orientation="vertical"
        class="-mx-2.5"
      />

      <!-- Phones: the header hides the language toggle, so offer it here. -->
      <UButton
        v-if="otherLocale"
        :label="otherLocale.name"
        :aria-label="t('common.changeLanguage')"
        icon="i-lucide-languages"
        color="neutral"
        variant="ghost"
        class="sm:hidden mt-3 -mx-2.5"
        @click="otherLocale && setLocale(otherLocale.code)"
      />

      <USeparator class="my-6" />

      <template v-if="authStore.isLoggedIn">
        <UButton
          :label="t('common.dashboard')"
          icon="i-lucide-layout-dashboard"
          color="primary"
          to="/app/dashboard"
          block
          class="mb-3"
        />
        <UButton
          :label="t('common.signOut')"
          icon="i-lucide-log-out"
          color="neutral"
          variant="subtle"
          block
          @click="async () => { await useSession().logout(); await navigateTo('/') }"
        />
      </template>
      <template v-else>
        <UButton
          :label="t('common.signIn')"
          color="neutral"
          variant="subtle"
          to="/login"
          block
          class="mb-3"
        />
        <UButton
          :label="t('common.register')"
          color="primary"
          to="/signup"
          block
        />
      </template>
    </template>
  </UHeader>
</template>
